/**
 * Engaging Networks AJAX Form Submission Service
 *
 * Handles async AJAX submission to EN data capture pages.
 * Extracts pageJson from response for debugging and handles errors.
 * 
 * Per EN documentation: https://knowledge.engagingnetworks.net/datareports/form-posting-sample-code
 * - Always POST to page 2 (final page)
 * - Field names must match exactly
 * - Cannot POST to pages with Session Validation (returns 403)
 * - Rate limit: 200 posts per 5 minutes
 */

import { getENFormConfig } from '../config/enFormConfig';
import { shouldBypassENSubmission, logENSubmissionData } from '../utils/enDevBypass';

export interface ENError {
  message: string;
  field?: string;
}

export interface SignupFormData {
  email: string;
  firstName: string;
  lastName: string;
  country: string;
  marketingConsent: boolean;
}

export interface ENSubmissionResponse {
  success: boolean;
  html?: string;
  pageJson?: unknown;
  errors?: ENError[];
}

/**
 * Removes script tags with src attributes from HTML to prevent 404 errors
 * We only need inline scripts for pageJson extraction
 */
function removeExternalScripts(html: string): string {
  // Remove script tags with src attributes (external scripts)
  // Handles both self-closing and regular script tags
  // This prevents browser from trying to load them and causing 404 errors
  return html
    .replace(/<script[^>]*\ssrc=["'][^"']*["'][^>]*>[\s\S]*?<\/script>/gi, '') // Regular script tags
    .replace(/<script[^>]*\ssrc=["'][^"']*["'][^>]*\/>/gi, '') // Self-closing script tags
    .replace(/<script[^>]*\ssrc=["'][^"']*["'][^>]*>/gi, ''); // Script tags without closing tag
}

/**
 * Extracts pageJson from response HTML
 * Looks for <script> tag containing `var pageJson = {...}`
 * Note: pageJson is JavaScript, not JSON, so we need to parse it carefully
 */
function extractPageJson(html: string): unknown | null {
  try {
    // Remove external script tags to prevent 404 errors
    const cleanedHtml = removeExternalScripts(html);
    
    // Match script tag with pageJson variable - capture the entire object
    // Pattern: var pageJson = { ... };
    const match = cleanedHtml.match(/var\s+pageJson\s*=\s*({[\s\S]*?});/);
    if (match && match[1]) {
      let jsonStr = match[1].trim();
      
      // Convert JavaScript object to JSON-like string
      // Replace single quotes with double quotes
      jsonStr = jsonStr.replace(/'/g, '"');
      
      // Handle unquoted property names (JavaScript allows this, JSON doesn't)
      jsonStr = jsonStr.replace(/([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:/g, '$1"$2":');
      
      // Remove trailing commas before closing braces/brackets
      jsonStr = jsonStr.replace(/,(\s*[}\]])/g, '$1');
      
      // Try to parse as JSON
      try {
        const parsed = JSON.parse(jsonStr);
        return parsed;
      } catch (parseError) {
        // If JSON parsing fails, try to extract via safer DOM parsing
        try {
          const parser = new DOMParser();
          const doc = parser.parseFromString(cleanedHtml, 'text/html');
          const scripts = doc.querySelectorAll('script');
          
          for (const script of scripts) {
            const scriptText = script.textContent || '';
            if (scriptText.includes('var pageJson') || scriptText.includes('pageJson =')) {
              // Try to extract via regex and manual parsing
              // This is safer than executing the script
              const pageJsonMatch = scriptText.match(/pageJson\s*=\s*({[\s\S]*?});/);
              if (pageJsonMatch && pageJsonMatch[1]) {
                // Try the conversion again with the matched content
                let cleanStr = pageJsonMatch[1].trim();
                cleanStr = cleanStr.replace(/'/g, '"');
                cleanStr = cleanStr.replace(/([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:/g, '$1"$2":');
                cleanStr = cleanStr.replace(/,(\s*[}\]])/g, '$1');
                
                try {
                  return JSON.parse(cleanStr);
                } catch {
                  // If still fails, return raw for debugging
                  return { raw: pageJsonMatch[1] };
                }
              }
            }
          }
        } catch (domError) {
          console.warn('[EN AJAX] DOM parsing failed:', domError);
        }
        
        // Last resort: log the raw match for manual inspection
        console.log('[EN AJAX] Could not parse pageJson, raw match:', match[1]);
        return { raw: match[1], parseError: String(parseError) };
      }
    }
  } catch (error) {
    console.warn('[EN AJAX] Failed to extract pageJson:', error);
  }
  return null;
}

/**
 * Parses EN errors from response HTML
 * Looks for <ul class="en__errorList"> elements
 */
function parseENErrors(html: string): ENError[] {
  const errors: ENError[] = [];
  
  try {
    // Remove external script tags to prevent 404 errors
    const cleanedHtml = removeExternalScripts(html);
    
    // Create a temporary DOM parser
    const parser = new DOMParser();
    const doc = parser.parseFromString(cleanedHtml, 'text/html');
    
    // Find error list
    const errorList = doc.querySelector('ul.en__errorList');
    if (errorList) {
      const listItems = errorList.querySelectorAll('li');
      listItems.forEach((item) => {
        const message = item.textContent?.trim();
        if (message) {
          errors.push({ message });
        }
      });
    }
  } catch (error) {
    console.warn('[EN AJAX] Failed to parse errors from HTML:', error);
  }
  
  return errors;
}

/**
 * Submits signup form data to EN via AJAX
 * @param formData Signup form data
 * @param language Language code (e.g., 'en', 'pl')
 * @returns Promise with submission response
 */
export async function submitENFormAjax(
  formData: SignupFormData,
  language: string
): Promise<ENSubmissionResponse> {
  try {
    // Get configuration for language
    const config = getENFormConfig(language);
    
    // Build URLSearchParams for application/x-www-form-urlencoded (standard HTML form format)
    // Engaging Networks expects this format, not multipart/form-data
    const formDataToSubmit = new URLSearchParams();
    
    // Map signup fields to EN field names
    // Per EN documentation: Only submit the fields that exist on the form
    // Do not include sendOffset, sessionId, or hidden fields unless they're on the form
    formDataToSubmit.append(config.fieldMappings.email, formData.email);
    formDataToSubmit.append(config.fieldMappings.firstName, formData.firstName);
    formDataToSubmit.append(config.fieldMappings.lastName, formData.lastName);
    formDataToSubmit.append(config.fieldMappings.country, formData.country);
    
    // Handle marketing consent
    // For Polish forms, this is a mandatory radio button field (Y/N)
    // For English forms, this is an optional checkbox (Y if checked)
    // Always send the value: 'Y' if consent given, 'N' if not
    const consentValue = formData.marketingConsent ? 'Y' : 'N';
    formDataToSubmit.append(config.fieldMappings.marketingConsent, consentValue);
    
    // Determine full URL (handle relative and absolute URLs)
    // Note: Per EN docs, we must always POST to page 2 (final page)
    const endpoint = config.endpoint.startsWith('http')
      ? config.endpoint
      : `${window.location.origin}${config.endpoint}`;
    
    // Check if we should bypass EN submission in dev mode
    if (shouldBypassENSubmission()) {
      logENSubmissionData(endpoint, formDataToSubmit, 'signup');
      
      // Return mock success response
      return {
        success: true,
        html: '<div>Mock EN response (dev bypass)</div>',
        pageJson: { pageNumber: 2, bypassed: true },
      };
    }
    
    // Enhanced debugging: Log exact payload being sent
    console.log('[EN AJAX] ===== SUBMISSION DEBUG START =====');
    console.log('[EN AJAX] Endpoint:', endpoint);
    console.log('[EN AJAX] Method: POST');
    
    // Log URLSearchParams entries before conversion
    console.log('[EN AJAX] URLSearchParams entries:');
    formDataToSubmit.forEach((value, key) => {
      console.log(`[EN AJAX]   ${key} = ${value}`);
    });
    
    // Log as object for readability
    console.log('[EN AJAX] Form data (as object):', Object.fromEntries(formDataToSubmit));
    
    // Log the raw body string (what will actually be sent)
    const bodyString = formDataToSubmit.toString();
    console.log('[EN AJAX] Raw body string:', bodyString);
    
    // Log what browser form would send (for comparison)
    console.log('[EN AJAX] Expected browser form encoding:', bodyString);
    console.log('[EN AJAX] ===== SUBMISSION DEBUG END =====');
    
    // Submit via AJAX
    // Pass URLSearchParams directly to fetch - let fetch handle Content-Type automatically
    // This matches how browser form submissions work
    const response = await fetch(endpoint, {
      method: 'POST',
      body: formDataToSubmit
    });
    
    // Get response as text (HTML)
    const html = await response.text();
    
    // Extract pageJson for debugging
    const pageJson = extractPageJson(html);
    if (pageJson) {
      console.log('[EN AJAX] pageJson:', pageJson);
    } else {
      console.warn('[EN AJAX] Could not extract pageJson from response');
    }
    
    // Check for errors in response
    const errors = parseENErrors(html);
    
    if (errors.length > 0) {
      console.error('[EN AJAX] Validation errors found:', errors);
      return {
        success: false,
        html,
        pageJson: pageJson || undefined,
        errors,
      };
    }
    
    // Check HTTP status
    if (!response.ok) {
      console.error('[EN AJAX] HTTP error:', response.status, response.statusText);
      
      // Specific handling for Session Validation error (per EN docs)
      // Pages with Session Validation enabled cannot be posted to via AJAX
      if (response.status === 403) {
        return {
          success: false,
          html,
          pageJson: pageJson || undefined,
          errors: [
            {
              message: 'This form cannot be submitted via AJAX because Session Validation is enabled on the Engaging Networks page. Please contact support.',
            },
          ],
        };
      }
      
      return {
        success: false,
        html,
        pageJson: pageJson || undefined,
        errors: [
          {
            message: `Server error: ${response.status} ${response.statusText}`,
          },
        ],
      };
    }
    
    // Success
    console.log('[EN AJAX] Submission successful');
    return {
      success: true,
      html,
      pageJson: pageJson || undefined,
    };
  } catch (error) {
    console.error('[EN AJAX] Network or parsing error:', error);
    return {
      success: false,
      errors: [
        {
          message:
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred during submission',
        },
      ],
    };
  }
}

/**
 * Default email address for anonymous submissions
 */
const DEFAULT_ANONYMOUS_EMAIL = 'anonymous-eu@noaddress.ea';

/**
 * Gets the current user's timezone offset for EN forms
 * EN expects this in the format like "-04:00" or "+01:00"
 */
function getTimezoneOffset(): string {
  const offset = -new Date().getTimezoneOffset();
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  const sign = offset >= 0 ? '+' : '-';
  
  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * Submits comment to EN via AJAX
 * Submits all required fields from the comment form (hidden, email, sendOffset, comment)
 * Matches the complete form structure from en-comment-form.html
 * @param comment User's comment text
 * @param email User's email address (uses default if empty)
 * @param language Language code (e.g., 'en', 'pl')
 * @returns Promise with submission response
 */
export async function submitCommentToEN(
  comment: string,
  email: string,
  language: string
): Promise<ENSubmissionResponse> {
  console.log('[EN AJAX Comment] Function called with:', {
    commentLength: comment.length,
    email: email.substring(0, 3) + '***',
    language
  });
  
  try {
    // Get configuration for language
    console.log('[EN AJAX Comment] Getting config for language:', language);
    const config = getENFormConfig(language);
    console.log('[EN AJAX Comment] Config retrieved:', {
      hasCommentField: !!config.commentField,
      commentField: config.commentField,
      endpoint: config.endpoint
    });
    
    // Check if comment field is configured for this language
    if (!config.commentField) {
      console.warn('[EN AJAX Comment] Comment field not configured for language:', language);
      return {
        success: false,
        errors: [
          {
            message: 'Comment submission is not available for this language.',
          },
        ],
      };
    }
    
    // Use default email if not provided
    const emailToUse = email.trim() || DEFAULT_ANONYMOUS_EMAIL;
    
    // Build URLSearchParams for application/x-www-form-urlencoded
    // Include all fields that exist in the HTML form (per en-comment-form.html)
    const formDataToSubmit = new URLSearchParams();
    
    // Required fields from the form:
    // 1. hidden field (empty string, but required by form structure)
    formDataToSubmit.append('hidden', '');
    
    // 2. Email address
    formDataToSubmit.append(config.fieldMappings.email, emailToUse);
    
    // 3. Timezone offset (required by EN forms)
    formDataToSubmit.append('supporter.sendOffset', getTimezoneOffset());
    
    // 4. Comment field
    formDataToSubmit.append(config.commentField, comment);
    
    // Note: sessionId is typically generated by EN and not needed for AJAX submission
    
    // Use unified endpoint (same as signup and completion)
    const endpoint = config.endpoint.startsWith('http')
      ? config.endpoint
      : `${window.location.origin}${config.endpoint}`;
    
    // Check if we should bypass EN submission in dev mode
    if (shouldBypassENSubmission()) {
      logENSubmissionData(endpoint, formDataToSubmit, 'comment');
      
      // Return mock success response
      return {
        success: true,
        html: '<div>Mock EN comment response (dev bypass)</div>',
        pageJson: { pageNumber: 1, bypassed: true },
      };
    }
    
    // Enhanced debugging: Log exact payload being sent
    console.log('[EN AJAX Comment] ===== SUBMISSION DEBUG START =====');
    console.log('[EN AJAX Comment] Endpoint:', endpoint);
    console.log('[EN AJAX Comment] Method: POST');
    
    // Log URLSearchParams entries before conversion
    console.log('[EN AJAX Comment] URLSearchParams entries:');
    formDataToSubmit.forEach((value, key) => {
      console.log(`[EN AJAX Comment]   ${key} = ${value}`);
    });
    
    // Log as object for readability
    console.log('[EN AJAX Comment] Form data (as object):', Object.fromEntries(formDataToSubmit));
    
    // Log the raw body string (what will actually be sent)
    const bodyString = formDataToSubmit.toString();
    console.log('[EN AJAX Comment] Raw body string:', bodyString);
    
    // Log what browser form would send (for comparison)
    console.log('[EN AJAX Comment] Expected browser form encoding:', bodyString);
    console.log('[EN AJAX Comment] ===== SUBMISSION DEBUG END =====');
    
    // Submit via AJAX
    // Pass URLSearchParams directly to fetch - let fetch handle Content-Type automatically
    // Include credentials to ensure cookies and session data are sent (matches browser form submissions)
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formDataToSubmit,
      credentials: 'include'
    });
    
    // Get response as text (HTML)
    const html = await response.text();
    
    // Extract pageJson for debugging
    const pageJson = extractPageJson(html);
    if (pageJson) {
      console.log('[EN AJAX Comment] pageJson:', pageJson);
    } else {
      console.warn('[EN AJAX Comment] Could not extract pageJson from response');
    }
    
    // Check for errors in response
    const errors = parseENErrors(html);
    
    if (errors.length > 0) {
      console.error('[EN AJAX Comment] Validation errors found:', errors);
      return {
        success: false,
        html,
        pageJson: pageJson || undefined,
        errors,
      };
    }
    
    // Check HTTP status
    if (!response.ok) {
      console.error('[EN AJAX Comment] HTTP error:', response.status, response.statusText);
      
      // Specific handling for Session Validation error (per EN docs)
      if (response.status === 403) {
        return {
          success: false,
          html,
          pageJson: pageJson || undefined,
          errors: [
            {
              message: 'This form cannot be submitted via AJAX because Session Validation is enabled on the Engaging Networks page. Please contact support.',
            },
          ],
        };
      }
      
      return {
        success: false,
        html,
        pageJson: pageJson || undefined,
        errors: [
          {
            message: `Server error: ${response.status} ${response.statusText}`,
          },
        ],
      };
    }
    
    // Success
    console.log('[EN AJAX Comment] Submission successful');
    return {
      success: true,
      html,
      pageJson: pageJson || undefined,
    };
  } catch (error) {
    console.error('[EN AJAX Comment] Network or parsing error:', error);
    return {
      success: false,
      errors: [
        {
          message:
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred during submission',
        },
      ],
    };
  }
}

/**
 * Submits completion data to EN via AJAX
 * Submits email + completion field to signup endpoint
 * @param email User's email address (uses default if empty)
 * @param timeInSeconds Total time elapsed in seconds
 * @param language Language code (e.g., 'en', 'pl')
 * @returns Promise with submission response
 */
export async function submitCompletionToEN(
  email: string,
  timeInSeconds: number,
  language: string
): Promise<ENSubmissionResponse> {
  console.log('[EN AJAX Completion] Function called with:', {
    email: email.substring(0, 3) + '***',
    timeInSeconds,
    language
  });
  
  try {
    // Get configuration for language
    console.log('[EN AJAX Completion] Getting config for language:', language);
    const config = getENFormConfig(language);
    console.log('[EN AJAX Completion] Config retrieved:', {
      hasCompletionField: !!config.completionField,
      completionField: config.completionField,
      endpoint: config.endpoint
    });
    
    // Check if completion field is configured for this language
    if (!config.completionField) {
      console.warn('[EN AJAX Completion] Completion field not configured for language:', language);
      return {
        success: false,
        errors: [
          {
            message: 'Completion submission is not available for this language.',
          },
        ],
      };
    }
    
    // Use default email if not provided
    const emailToUse = email.trim() || DEFAULT_ANONYMOUS_EMAIL;
    
    // Calculate minutes and seconds
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    
    // Format completion value as "Complete~{minutes}:{seconds}"
    const completionValue = `Complete~${minutes}:${String(seconds).padStart(2, '0')}`;
    
    // Build URLSearchParams for application/x-www-form-urlencoded
    // Only email + completion field (simpler than comment submission)
    const formDataToSubmit = new URLSearchParams();
    
    // 1. Email address
    formDataToSubmit.append(config.fieldMappings.email, emailToUse);
    
    // 2. Completion field
    formDataToSubmit.append(config.completionField, completionValue);
    
    // Use signup endpoint (same as submitENFormAjax)
    const endpoint = config.endpoint.startsWith('http')
      ? config.endpoint
      : `${window.location.origin}${config.endpoint}`;
    
    // Check if we should bypass EN submission in dev mode
    if (shouldBypassENSubmission()) {
      logENSubmissionData(endpoint, formDataToSubmit, 'completion');
      
      // Return mock success response
      return {
        success: true,
        html: '<div>Mock EN completion response (dev bypass)</div>',
        pageJson: { pageNumber: 2, bypassed: true },
      };
    }
    
    // Enhanced debugging: Log exact payload being sent
    console.log('[EN AJAX Completion] ===== SUBMISSION DEBUG START =====');
    console.log('[EN AJAX Completion] Endpoint:', endpoint);
    console.log('[EN AJAX Completion] Method: POST');
    
    // Log URLSearchParams entries before conversion
    console.log('[EN AJAX Completion] URLSearchParams entries:');
    formDataToSubmit.forEach((value, key) => {
      console.log(`[EN AJAX Completion]   ${key} = ${value}`);
    });
    
    // Log as object for readability
    console.log('[EN AJAX Completion] Form data (as object):', Object.fromEntries(formDataToSubmit));
    
    // Log the raw body string (what will actually be sent)
    const bodyString = formDataToSubmit.toString();
    console.log('[EN AJAX Completion] Raw body string:', bodyString);
    
    // Log completion time breakdown
    console.log('[EN AJAX Completion] Time breakdown:', {
      totalSeconds: timeInSeconds,
      minutes,
      seconds,
      formatted: completionValue
    });
    
    // Log what browser form would send (for comparison)
    console.log('[EN AJAX Completion] Expected browser form encoding:', bodyString);
    console.log('[EN AJAX Completion] ===== SUBMISSION DEBUG END =====');
    
    // Submit via AJAX
    // Pass URLSearchParams directly to fetch - let fetch handle Content-Type automatically
    // Include credentials to ensure cookies and session data are sent (matches browser form submissions)
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formDataToSubmit,
      credentials: 'include'
    });
    
    // Get response as text (HTML)
    const html = await response.text();
    
    // Extract pageJson for debugging
    const pageJson = extractPageJson(html);
    if (pageJson) {
      console.log('[EN AJAX Completion] pageJson:', pageJson);
    } else {
      console.warn('[EN AJAX Completion] Could not extract pageJson from response');
    }
    
    // Check for errors in response
    const errors = parseENErrors(html);
    
    if (errors.length > 0) {
      console.error('[EN AJAX Completion] Validation errors found:', errors);
      return {
        success: false,
        html,
        pageJson: pageJson || undefined,
        errors,
      };
    }
    
    // Check HTTP status
    if (!response.ok) {
      console.error('[EN AJAX Completion] HTTP error:', response.status, response.statusText);
      
      // Specific handling for Session Validation error (per EN docs)
      if (response.status === 403) {
        return {
          success: false,
          html,
          pageJson: pageJson || undefined,
          errors: [
            {
              message: 'This form cannot be submitted via AJAX because Session Validation is enabled on the Engaging Networks page. Please contact support.',
            },
          ],
        };
      }
      
      return {
        success: false,
        html,
        pageJson: pageJson || undefined,
        errors: [
          {
            message: `Server error: ${response.status} ${response.statusText}`,
          },
        ],
      };
    }
    
    // Success
    console.log('[EN AJAX Completion] Submission successful');
    return {
      success: true,
      html,
      pageJson: pageJson || undefined,
    };
  } catch (error) {
    console.error('[EN AJAX Completion] Network or parsing error:', error);
    return {
      success: false,
      errors: [
        {
          message:
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred during submission',
        },
      ],
    };
  }
}

